/**
 * Darshan Service
 * Handles all daily darshan media-related API calls
 */

import { ApiClient } from '../core/api-client';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export type DarshanType = 'Sringar' | 'Mangala' | 'Festival';

export interface DarshanMedia {
  id: number;
  type: DarshanType;
  date: string;
  mediaType: 'image' | 'video';
  url: string;
  title?: string;
  order?: number;
}

export interface DarshanParams {
  date?: string;
  days?: number;
}

class DarshanService {
  private readonly client: ApiClient;

  constructor(client: ApiClient) {
    this.client = client;
  }

  /**
   * Get darshan media by type
   */
  async getByType(
    type: DarshanType,
    params: DarshanParams = {},
    signal?: AbortSignal,
  ): Promise<DarshanMedia[]> {
    try {
      const search = new URLSearchParams();
      if (params.date) search.set('date', params.date);
      if (params.days !== undefined) search.set('days', String(params.days));

      const query = search.toString();
      const basePath = `/darshan/${encodeURIComponent(type)}`;
      const endpoint = query ? `${basePath}?${query}` : basePath;

      const response = await this.client.get<unknown>(endpoint, { signal });
      return Array.isArray(response.data) ? (response.data as DarshanMedia[]) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Get Sringar darshan
   */
  async getSringar(params?: DarshanParams, signal?: AbortSignal): Promise<DarshanMedia[]> {
    return this.getByType('Sringar', params, signal);
  }

  /**
   * Get Mangala darshan
   */
  async getMangala(params?: DarshanParams, signal?: AbortSignal): Promise<DarshanMedia[]> {
    return this.getByType('Mangala', params, signal);
  }

  /**
   * Get Festival darshan
   */
  async getFestival(params?: DarshanParams, signal?: AbortSignal): Promise<DarshanMedia[]> {
    return this.getByType('Festival', params, signal);
  }
}

export const darshanService = new DarshanService(ApiClient.getInstance(API_BASE_URL));
